import { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { FiActivity, FiClock, FiCheck, FiX, FiPlay } from 'react-icons/fi';

import { trilhaGenerationsApi } from '@/services/trilhaGenerations';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';

// Polling interval (ms). Mesmo padrão do BatchProgressModal.
const POLL_INTERVAL = 3000;

const STATUS_CONFIG = {
  queued: { label: 'Na fila', variant: 'neutral-lightest', icon: <FiClock /> },
  running: { label: 'Gerando', variant: 'warning', icon: <FiPlay /> },
  done: { label: 'Concluída', variant: 'success', icon: <FiCheck /> },
  failed: { label: 'Falhou', variant: 'danger', icon: <FiX /> }
};

const ACTIVE_STATUSES = ['queued', 'running'];

const TAB_DEFS = [
  { value: 'active', label: 'Em andamento', statuses: ['queued', 'running'] },
  { value: 'done', label: 'Concluídas', statuses: ['done'] },
  { value: 'failed', label: 'Falhas', statuses: ['failed'] }
];

function formatDateTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('pt-BR');
}

function elapsedMinutes(startedAt) {
  if (!startedAt) return null;
  const ms = Date.now() - new Date(startedAt).getTime();
  return Math.max(0, Math.round(ms / 60000));
}

function progressBarClass(status) {
  switch (status) {
    case 'done':
      return 'bg-success';
    case 'failed':
      return 'bg-danger';
    case 'running':
      return 'bg-warning animate-pulse';
    default:
      return 'bg-neutral-light';
  }
}

/**
 * Acompanhamento das gerações de trilha via IA.
 * Polling em GET /api/v1/admin/trilha_generations a cada 3 s enquanto aberto.
 */
export default function TrilhaGenerationsModal({ open, onOpenChange, highlightId }) {
  const [generations, setGenerations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('active');
  const activeRef = useRef(false);

  const load = useCallback(async () => {
    try {
      const json = await trilhaGenerationsApi.index();
      if (!activeRef.current) return;
      setGenerations(json?.data || []);
    } catch (e) {
      if (!activeRef.current) return;
      toast.error('Erro ao carregar gerações de trilha', { id: 'trilha-gen-poll' });
    } finally {
      if (activeRef.current) setLoading(false);
    }
  }, []);

  // Carrega ao abrir e configura polling enquanto aberta.
  useEffect(() => {
    if (!open) return;
    activeRef.current = true;
    setLoading(true);
    setActiveTab('active'); // ao reabrir, volta para "Em andamento"
    load();
    const id = setInterval(load, POLL_INTERVAL);
    return () => {
      activeRef.current = false;
      clearInterval(id);
    };
  }, [open, load]);

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
    >
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-primary-dark">
            <FiActivity /> Gerações de trilha
          </DialogTitle>
          <DialogDescription>
            A geração roda em background (≈ 3–4 min por bloco novo). Esta lista atualiza a cada 3 segundos.
            {generations.some(g => ACTIVE_STATUSES.includes(g.attributes.status)) && <span className="text-warning-dark ml-2">● ativa</span>}
          </DialogDescription>
        </DialogHeader>

        {loading && generations.length === 0 ? (
          <p className="py-4 text-center text-sm text-neutral">Carregando...</p>
        ) : (
          <Tabs
            value={activeTab}
            onValueChange={setActiveTab}
          >
            <TabsList>
              {TAB_DEFS.map(t => {
                const count = generations.filter(g => t.statuses.includes(g.attributes.status)).length;
                return (
                  <TabsTrigger
                    key={t.value}
                    value={t.value}
                  >
                    {t.label} {count > 0 && <span className="ml-1 text-xs opacity-70">({count})</span>}
                  </TabsTrigger>
                );
              })}
            </TabsList>

            {TAB_DEFS.map(t => {
              const list = generations.filter(g => t.statuses.includes(g.attributes.status));
              return (
                <TabsContent
                  key={t.value}
                  value={t.value}
                >
                  {list.length === 0 ? (
                    <p className="py-4 text-center text-sm text-neutral">{t.value === 'active' ? 'Nenhuma geração em andamento. Use "Nova trilha" para iniciar.' : 'Nenhuma geração nesta aba.'}</p>
                  ) : (
                    <ul className="max-h-[55vh] space-y-3 overflow-y-auto pr-2">
                      {list.map(g => (
                        <GenerationCard
                          key={g.id}
                          generation={g}
                          highlighted={highlightId && String(g.id) === String(highlightId)}
                        />
                      ))}
                    </ul>
                  )}
                </TabsContent>
              );
            })}
          </Tabs>
        )}

        <div className="mt-4 flex justify-end">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Fechar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function GenerationCard({ generation, highlighted }) {
  const a = generation.attributes;
  const cfg = STATUS_CONFIG[a.status] || STATUS_CONFIG.queued;
  return (
    <li className={`rounded-lg border p-3 ${highlighted ? 'bg-primary/5 border-primary' : 'border-neutral-lightest bg-white'}`}>
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <Badge
          variant={cfg.variant}
          className="flex items-center gap-1"
        >
          {cfg.icon} {cfg.label}
        </Badge>
        {a.etapa_label && <Badge variant="primary-light">{a.etapa_label}</Badge>}
        <code className="text-xs text-neutral">#{generation.id}</code>
        {a.learning_path_name && <span className="text-xs text-neutral">· {a.learning_path_name}</span>}
        <span className="ml-auto text-xs text-neutral">Solicitada em {formatDateTime(a.created_at)}</span>
      </div>

      <div className="mb-2">
        <div className="mb-1 flex justify-between text-xs">
          <span>
            {a.blocks_generated} / {a.blocks_count} blocos
            {a.status === 'running' && elapsedMinutes(a.started_at) != null && <span className="ml-2 text-neutral">({elapsedMinutes(a.started_at)} min decorridos)</span>}
          </span>
          <span className="font-medium tabular-nums">{a.progress_pct ?? 0}%</span>
        </div>
        <div className="h-2 w-full overflow-hidden rounded bg-neutral-lightest">
          <div
            className={`h-full transition-all ${progressBarClass(a.status)}`}
            style={{ width: `${Math.min(100, a.progress_pct ?? 0)}%` }}
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-4 text-xs text-neutral">
        {a.requester?.name && <span>por {a.requester.name}</span>}
        {a.finished_at && <span>Finalizada em {formatDateTime(a.finished_at)}</span>}
      </div>

      {a.error_message && (
        <details className="bg-danger/5 mt-2 rounded p-2 text-xs">
          <summary className="cursor-pointer text-danger">Detalhes do erro</summary>
          <pre className="mt-1 whitespace-pre-wrap break-words">{a.error_message}</pre>
        </details>
      )}
    </li>
  );
}
